import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react'
import { ArrowUpRight, BarChart3, Check, Clock3, History, RefreshCw, Settings as SettingsIcon, X } from 'lucide-react'
import { api, ApiError, subscribeToUnauthorized } from './lib/api'
import { authMode, haptic, initializeTelegram } from './lib/telegram'
import type { AuthSessionDto, BootstrapDto, UserAction } from './types'
import DashboardPage from './pages/DashboardPage'
import Onboarding from './components/Onboarding'
import PageBoundary from './components/PageBoundary'

const StatisticsPage = lazy(() => import('./pages/StatisticsPage'))
const HistoryPage = lazy(() => import('./pages/HistoryPage'))
const SettingsPage = lazy(() => import('./pages/SettingsPage'))

type Tab = 'today' | 'statistics' | 'history' | 'settings'

const tabs = [
  { id: 'today' as const, label: 'Сегодня', icon: Clock3 },
  { id: 'statistics' as const, label: 'Статистика', icon: BarChart3 },
  { id: 'history' as const, label: 'История', icon: History },
  { id: 'settings' as const, label: 'Настройки', icon: SettingsIcon },
]

export default function App() {
  const [mode] = useState(authMode)
  const [tab, setTab] = useState<Tab>('today')
  const [data, setData] = useState<BootstrapDto>()
  const [webSession, setWebSession] = useState<AuthSessionDto>()
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string>()
  const [toast, setToast] = useState<string>()
  const [showOnboarding, setShowOnboarding] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [lastRefresh, setLastRefresh] = useState<Date>()
  const mainRef = useRef<HTMLElement>(null)
  const selectedTab = tabs.find((item) => item.id === tab)!

  const refresh = useCallback(async () => {
    try {
      setError(undefined)
      const next = await api.bootstrap()
      setData(next)
      setLastRefresh(new Date())
      const forced = new URLSearchParams(window.location.search).get('onboarding') === '1'
      setShowOnboarding(forced || !next.user.onboardingComplete)
    } catch (cause) {
      if (mode === 'WEB' && cause instanceof ApiError && cause.status === 401) {
        setWebSession({ authenticated: false, loginUrl: '/oauth2/authorization/telegram' })
        setData(undefined)
        return
      }
      setError(cause instanceof Error ? cause.message : 'Не удалось загрузить данные')
    } finally {
      setLoading(false)
    }
  }, [mode])

  useEffect(() => subscribeToUnauthorized(() => {
    setData(undefined)
    setTab('today')
    if (mode === 'WEB') {
      setWebSession({ authenticated: false, loginUrl: '/oauth2/authorization/telegram' })
      setError('Сессия истекла. Войдите через Telegram ещё раз.')
    } else {
      setError('Данные запуска Telegram устарели. Закройте Mini App и откройте его снова из меню бота.')
    }
  }), [mode])

  useEffect(() => {
    initializeTelegram()
    const start = async () => {
      if (mode === 'WEB') {
        try {
          const session = await api.authSession()
          setWebSession(session)
          if (!session.authenticated) {
            setLoading(false)
            return
          }
        } catch (cause) {
          setError(cause instanceof Error ? cause.message : 'Не удалось проверить авторизацию')
          setLoading(false)
          return
        }
      }
      await refresh()
    }
    void start()
  }, [mode, refresh])

  useEffect(() => {
    if (!toast) return
    const timer = window.setTimeout(() => setToast(undefined), 4500)
    return () => window.clearTimeout(timer)
  }, [toast])

  const runAction = async (action: UserAction) => {
    if (busy) return
    setBusy(true)
    setError(undefined)
    haptic('tap')
    try {
      const next = await api.action(action)
      setData(next)
      haptic('success')
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : 'Действие не выполнено'
      setError(message)
      haptic('error')
    } finally {
      setBusy(false)
    }
  }

  const showToast = (message: string) => {
    setToast(message)
    haptic('success')
  }

  const changeTab = (nextTab: Tab) => {
    if (nextTab === tab) return
    const navigation = new Event('office-tracker:before-navigate', { cancelable: true })
    if (!window.dispatchEvent(navigation)) return
    setTab(nextTab)
    haptic('select')
    window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: 'instant' })
      mainRef.current?.focus({ preventScroll: true })
    })
  }

  const refreshData = async () => {
    if (refreshing || busy) return
    setRefreshing(true)
    await refresh()
    setRefreshing(false)
  }

  useEffect(() => {
    document.title = `${selectedTab.label} · OfficeTracker`
  }, [selectedTab.label])

  if (loading) return <LoadingScreen />
  if (mode === 'WEB' && webSession && !webSession.authenticated) {
    return <LoginScreen loginUrl={webSession.loginUrl} loginConfigured={webSession.loginConfigured !== false} message={error} />
  }
  if (!data) return <FatalScreen message={error || 'Приложение временно недоступно'} retry={refresh} />

  return (
    <div className={`app-shell auth-${mode.toLowerCase()}`}>
      <a className="skip-link" href="#main-content" onClick={(event) => {
        event.preventDefault()
        mainRef.current?.focus({ preventScroll: true })
        mainRef.current?.scrollIntoView({ block: 'start' })
      }}>К содержимому</a>
      <aside className="desktop-rail" aria-label="OfficeTracker">
        <button className="rail-brand" onClick={() => changeTab('today')} aria-label="OfficeTracker — сегодня">
          <span className="rail-brand-icon"><Clock3 size={24} /></span>
          <span><b>OfficeTracker</b><small>Время для важного</small></span>
        </button>
        <span className="rail-section-label">Рабочее пространство</span>
        <nav className="rail-navigation" aria-label="Основная навигация">
          {tabs.map((item) => <NavigationButton key={item.id} item={item} active={tab === item.id} onClick={() => changeTab(item.id)} />)}
        </nav>
        <div className="rail-footer">
          <div className="rail-account"><span>{(data.user.firstName || data.user.username || 'U').slice(0, 1).toUpperCase()}</span><div><b>{data.user.firstName || data.user.username || 'Ваш аккаунт'}</b><small>{mode === 'MINI_APP' ? 'Telegram Mini App' : 'Веб-приложение'}</small></div></div>
          <p>{data.user.timeZone.replaceAll('_', ' ')}</p>
          <button className="rail-refresh" onClick={() => void refreshData()} disabled={refreshing || busy}>
            <RefreshCw size={16} className={refreshing ? 'is-spinning' : ''} />{refreshing ? 'Обновляю…' : 'Обновить данные'}
          </button>
          {lastRefresh && <small className="rail-refreshed">Обновлено в {new Intl.DateTimeFormat('ru-RU', { hour: '2-digit', minute: '2-digit', timeZone: data.user.timeZone }).format(lastRefresh)}</small>}
        </div>
      </aside>
      <main id="main-content" className="page-container" ref={mainRef} tabIndex={-1} aria-label={selectedTab.label}>
        <PageBoundary key={tab}>
        {tab === 'today' && (
          <DashboardPage
            data={data}
            busy={busy}
            onAction={runAction}
            onData={setData}
            onError={setError}
            onToast={showToast}
          />
        )}
        <Suspense fallback={<PageLoader />}>
          {tab === 'statistics' && <StatisticsPage timeZone={data.user.timeZone} activeWorkday={data.activeSession && data.todayPlan?.activeCycleId ? data.todayPlan.date : undefined} onError={setError} />}
          {tab === 'history' && <HistoryPage data={data} onError={setError} onToast={showToast} />}
          {tab === 'settings' && (
            <SettingsPage
              data={data}
              authMode={mode}
              onRefresh={refresh}
              onError={setError}
              onToast={showToast}
              onLogout={async () => {
                await api.logout()
                setData(undefined)
                setWebSession({ authenticated: false, loginUrl: '/oauth2/authorization/telegram' })
                setTab('today')
              }}
            />
          )}
        </Suspense>
        </PageBoundary>
      </main>

      <nav className="bottom-nav" aria-label="Основная навигация">
        {tabs.map((item) => <NavigationButton key={item.id} item={item} active={tab === item.id} onClick={() => changeTab(item.id)} />)}
      </nav>

      {error && (
        <button className="error-toast" role="alert" aria-live="assertive" onClick={() => setError(undefined)}>
          <span>{error}</span><X size={18} aria-label="Закрыть сообщение" />
        </button>
      )}
      {toast && <div className="success-toast" role="status" aria-live="polite"><Check size={18} /> {toast}</div>}

      {showOnboarding && (
        <Onboarding
          initial={data.settings}
          firstName={data.user.firstName}
          onComplete={async (settings) => {
            await api.saveSettings(settings, true)
            await refresh()
            setShowOnboarding(false)
            const cleanedUrl = new URL(window.location.href)
            cleanedUrl.searchParams.delete('onboarding')
            window.history.replaceState({}, '', `${cleanedUrl.pathname}${cleanedUrl.search}${cleanedUrl.hash}`)
            showToast('Настройки сохранены')
          }}
          onError={setError}
        />
      )}
    </div>
  )
}

function NavigationButton({ item, active, onClick }: { item: typeof tabs[number]; active: boolean; onClick: () => void }) {
  const Icon = item.icon
  return <button className={`nav-item${active ? ' active' : ''}`} aria-current={active ? 'page' : undefined} onClick={onClick}>
    <Icon size={22} strokeWidth={active ? 2.3 : 1.8} aria-hidden="true" />
    <span>{item.label}</span>
  </button>
}

function PageLoader() {
  return <div className="page" role="status" aria-label="Загружаем раздел"><div className="page-loading-label"><span className="loading-dot" /> Загружаем данные…</div><div className="history-skeleton" aria-hidden="true"><div /><div /><div /></div></div>
}

function LoadingScreen() {
  return (
    <div className="loading-screen" role="status" aria-label="Загружаем OfficeTracker">
      <div className="brand-mark"><span /></div>
      <h1>Мой рабочий день</h1>
      <div className="loading-bar"><span /></div>
    </div>
  )
}

function LoginScreen({ loginUrl, loginConfigured, message }: { loginUrl?: string; loginConfigured: boolean; message?: string }) {
  const failed = new URLSearchParams(window.location.search).has('loginError')
  return (
    <div className="login-screen">
      <div className="login-card">
        <div className="brand-mark"><span /></div>
        <span className="eyebrow">OfficeTracker</span>
        <h1>Рабочий день.<br />В вашем ритме.</h1>
        <p>Работа, перерывы, дорога и сон — в одном месте. Войдите через Telegram: история доступна и в браузере, и в Mini App.</p>
        {(failed || message) && <div className="login-error" role="alert">{message || 'Не удалось войти через Telegram. Попробуйте ещё раз.'}</div>}
        {loginConfigured ? <a className="button primary telegram-login" href={loginUrl || '/oauth2/authorization/telegram'}>
          Войти через Telegram <ArrowUpRight size={20} />
        </a> : <div className="login-error" role="alert">Вход через Telegram пока не настроен администратором.</div>}
        <small>Пароль Telegram не передаётся OfficeTracker.</small>
      </div>
    </div>
  )
}

function FatalScreen({ message, retry }: { message: string; retry: () => void }) {
  return (
    <div className="fatal-screen">
      <div className="fatal-icon">!</div>
      <h1>Не удалось открыть трекер</h1>
      <p>{message}</p>
      <button className="button primary" onClick={retry}>Попробовать снова</button>
      <small>Проверьте соединение и попробуйте ещё раз.</small>
    </div>
  )
}
